import { watch, type Ref } from 'vue';
import type { AxiosError } from 'axios';

import { api } from '@/shared/api';
import { queryClient } from '@/shared/lib/query-client';
import { sentryModel } from '@/shared/model';

import { PRODUCTS_WHITE_LIST } from './config';
import type { BookingError } from '../../types';

function fetchProducts(offerId: string) {
  return api.airflow.products(offerId).then(({ data }) => {
    return {
      items: data.items.filter(product =>
        PRODUCTS_WHITE_LIST.includes(product.service),
      ),
      packages: data.packages,
    };
  });
}

function waitForEnabled(enabled: Ref<boolean>): Promise<void> {
  if (enabled.value) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    const stop = watch(enabled, value => {
      if (value) {
        stop();
        resolve();
      }
    });
  });
}

/**
 * Uses the shared `queryClient.fetchQuery` so the call site is not bound to a
 * Vue setup context — QuickBooking re-invokes this after every fare-change to
 * refresh fare-bound items/packages, while ProductsPage (classic flow) calls
 * it once at mount. The optional `enabled` ref gates the actual HTTP behind
 * QuickBooking's "wait until the fare-change settles" signal.
 *
 * Why staleTime: Infinity?
 * Server-side product IDs change on every refetch — the productsModel state
 * is keyed by those IDs, so unsolicited refetches would invalidate the user's
 * selections. Callers opt into a refresh explicitly by invalidating the cache
 * and calling `products.init()` again (see useQuickBooking.onFareChange);
 * fetchQuery then sees the query as stale and re-runs queryFn.
 */
export async function getProducts(offerId: string, enabled?: Ref<boolean>) {
  if (enabled) {
    await waitForEnabled(enabled);
  }
  try {
    return await queryClient.fetchQuery({
      queryKey: ['products', offerId],
      queryFn: () => fetchProducts(offerId),
      staleTime: Infinity,
    });
  } catch (error) {
    sentryModel.logBaseError({
      error: error as AxiosError<{ errors: BookingError[] }>,
      description: 'Booking error: failed to fetch products',
    });
    throw error;
  }
}
